"use client";

import { useEffect, useState } from "react";
import arrowMobile from "@/assets/fahrzeug-bewertungs-tool/arrow-mobile.svg";
import Image from "next/image";
import "@/app/fahrzeug-bewertungs-tool/[fin]/FahrzeugBewertungsTool.css";
import { formatNumber } from "@/helpers/formatNumber";
import { getDisplayValuation } from "@/helpers/getDisplayValuation";
import BrandLogo from "@/app/Home/homeSections/brandLogo";
import useIsMobile from "@/app/helpers/useIsMobile";

export const TopSectionLiveVerkauf = ({
  carData,
  showEstimatedPrice = true,
  // Optional: abweichendes Label/abweichender Wert in der gelben Leiste
  // (z.B. "Unser vorgeschlagener Richtpreis" auf der Richtpreis-Seite).
  priceLabel = "Geschätzter Verkaufspreis",
  priceOverride = null,
  priceInfoText = "Basierend auf Echtzeit-Marktwerten und Daten von über 5.000 geprüften Händlern.",
}) => {
  const [isDetailsShown, setIsDetailsShown] = useState(false);
  const isMobile = useIsMobile();
  const [showBrand, setShowBrand] = useState(false);
  const [isPriceInfoOpen, setIsPriceInfoOpen] = useState(false);
  const displayPrice =
    priceOverride !== null && priceOverride !== undefined
      ? priceOverride
      : getDisplayValuation(carData).price;
  const hasPrice =
    displayPrice !== undefined &&
    displayPrice !== null &&
    String(displayPrice).trim() !== "";

  // Preis-Info-Box: schliessen bei Klick ausserhalb oder Escape.
  useEffect(() => {
    if (!isPriceInfoOpen) return;
    const onDocClick = (e) => {
      if (!e.target?.closest?.(".FBT-price-info")) setIsPriceInfoOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setIsPriceInfoOpen(false);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [isPriceInfoOpen]);

  // Label "Geschätzter Verkaufspreis" + "?"-Info-Box. Wird sowohl im
  // Mobile- als auch im Desktop-Preisblock verwendet (nur einer rendert je).
  const priceInfoLabel = (
    <span className="FBT-estimated-price-label">
      {priceLabel}
      <button
        type="button"
        className="FBT-price-info"
        aria-label={priceInfoText}
        aria-expanded={isPriceInfoOpen}
        onClick={() => setIsPriceInfoOpen((open) => !open)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 48 48"
          aria-hidden="true"
          className="FBT-price-info-icon"
        >
          {/* "?"-Icon im Referenz-Stil, Outline ohne weisse Fuellung. */}
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M24 4.02a20 20 0 1 0 0 40 20 20 0 0 0 0-40m0 37.4c-9.59 0-17.4-7.81-17.4-17.4S14.41 6.62 24 6.62s17.4 7.81 17.4 17.4-7.81 17.4-17.4 17.4"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M24 36a2 2 0 1 0 0-4 2 2 0 0 0 0 4m1.56-23.82a7.31 7.31 0 0 0-8.83 7.12 1.3 1.3 0 1 0 2.6 0 4.66 4.66 0 0 1 5.7-4.57 4.68 4.68 0 0 1 .86 8.85 5.36 5.36 0 0 0-3.18 4.92 1.3 1.3 0 1 0 2.6 0c0-1.11.64-2.1 1.63-2.54a7.26 7.26 0 0 0-1.38-13.77z"
          />
        </svg>
        <span
          className={`FBT-price-info-bubble ${isPriceInfoOpen ? "open" : ""}`}
          role="tooltip"
        >
          {priceInfoText}
        </span>
      </button>
    </span>
  );

  return (
    <div className="FahrzeugBewertungsToolTop">
      <div className="FahrzeugBewertungsToolTop-wrapper">
        {carData && (
          <>
            <div className="FBT-FIN-wrapper">
              {isMobile &&
                (showEstimatedPrice && hasPrice ? (
                  <>
                    {/* Mit Preis: geschätzter Preis links, Logo + Dropdown
                        rechts (wie im Referenzdesign). */}
                    <div className="FBT-mobile-price-row">
                      <div className="FBT-estimated-price FBT-estimated-price-mobile">
                        {priceInfoLabel}
                        <span className="FBT-estimated-price-value">
                          {formatNumber(displayPrice)}€
                        </span>
                      </div>
                    </div>

                    <div className="FBT-mobile-right">
                      <BrandLogo
                        onSuccess={() => setShowBrand(false)}
                        onError={() => setShowBrand(true)}
                        brand={carData.marke}
                        width={50}
                        height={50}
                      />
                      {showBrand && (
                        <h3 className="FBT-FIN-Brand">{carData.marke}</h3>
                      )}
                      <div
                        className="FBT-FIN-arrow-wrapper transition"
                        onClick={() => setIsDetailsShown(!isDetailsShown)}
                      >
                        <Image
                          src={arrowMobile}
                          alt="arrow"
                          className={`FBT-FIN-arrow  ${
                            isDetailsShown ? "open" : "closed"
                          }`}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Ohne Preis (z. B. Status-Seite, showEstimatedPrice=false):
                        unveraendertes Layout – Logo links, Dropdown rechts.
                        Der Markennamen-Fallback (Marke ohne Logo) MUSS mit in
                        die Row: als eigenes Flex-Kind wuerde space-between ihn
                        zwischen leerer Row und Pfeil mittig zentrieren. */}
                    <div className="FBT-mobile-price-row">
                      <BrandLogo
                        onSuccess={() => setShowBrand(false)}
                        onError={() => setShowBrand(true)}
                        brand={carData.marke}
                        width={50}
                        height={50}
                      />
                      {showBrand && (
                        <h3 className="FBT-FIN-Brand">{carData.marke}</h3>
                      )}
                    </div>

                    <div
                      className="FBT-FIN-arrow-wrapper transition"
                      onClick={() => setIsDetailsShown(!isDetailsShown)}
                    >
                      <Image
                        src={arrowMobile}
                        alt="arrow"
                        className={`FBT-FIN-arrow  ${
                          isDetailsShown ? "open" : "closed"
                        }`}
                      />
                    </div>
                  </>
                ))}
            </div>

            <div
              className={`FBT-right-wrap ${
                isDetailsShown ? "shown" : "hidden"
              }`}
            >
              {!isMobile && <BrandLogo brand={carData.marke} />}

              <div className="FBT-right">
                <div className="FBT-right-top">
                  <h1>
                    {carData?.marke || "Unknown marke"}{" "}
                    {carData?.model || "Unknown Model"}
                  </h1>
                </div>
                <div className="FBT-right-bottom">
                  <ul>
                    <li>{carData?.baujahr || ""}</li>
                    <li>
                      {carData?.kmStand
                        ? `${formatNumber(carData.kmStand)}km`
                        : ""}
                    </li>
                    <li>{carData?.vehicleFeatures?.bodytype || ""}</li>
                    <li>{carData?.gearbox || ""}</li>
                    <li>{carData?.fueltype || ""}</li>
                  </ul>
                </div>
              </div>

              {!isMobile && showEstimatedPrice && hasPrice && (
                <div className="FBT-estimated-price">
                  {priceInfoLabel}
                  <span className="FBT-estimated-price-value">
                    {formatNumber(displayPrice)}€
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
